import * as React from 'react'
import { useState } from 'react'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Link from '@mui/material/Link'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import { CircularProgress, Alert, Snackbar } from '@mui/material'
import GoogleIcon from '@mui/icons-material/Google'
import { ThemeProvider } from '@mui/material/styles'
import defaultTheme from '../shared-theme/defaultTheme'
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, sendEmailVerification } from 'firebase/auth'
import { auth } from '../../../firebase/firebase'
import { trackFailedLoginAttempt, checkAccountLockStatus, resetLoginAttempts } from '../../../firebase/accountLockoutService'

function Copyright(props) {
  return (
    <Typography variant='body2' color='text.secondary' align='center' {...props}>
      {'Copyright © '}
      <Link color='inherit' href='https://indabo.com/'>
        indabo
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  )
}

export default function SignIn({ onAuthStateChange }) {
  const [isSignUp, setIsSignUp] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' })

  const handleEmailSignIn = async (email, password) => {
    setLoading(true)
    setError('')
    setNotification(null)

    try {
      // Check if the account is locked
      const lockStatus = await checkAccountLockStatus(email)
      if (lockStatus.locked) {
        setError(lockStatus.error)
        setLoading(false)
        return
      }

      // Proceed with sign-in
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password)
        // Reset login attempts on successful login
        await resetLoginAttempts(email)
        if (onAuthStateChange) {
          onAuthStateChange(userCredential.user)
        }

        // Check if email is verified
        if (!userCredential.user.emailVerified) {
          setNotification({
            open: true,
            message: 'Please verify your email address to access all features.',
            severity: 'warning',
          })
        } else {
          setNotification({
            open: true,
            message: 'Sign in successful!',
            severity: 'success',
          })
        }
      } catch (signInError) {
        console.error('Sign-in error:', signInError)

        // Track the failed login attempt
        const trackResult = await trackFailedLoginAttempt(email)

        if (trackResult.locked) {
          setError(trackResult.error)
        } else if (trackResult.attemptsRemaining) {
          setError(`Invalid email or password. ${trackResult.attemptsRemaining} attempts remaining before temporary lockout.`)
        } else {
          // Fall back to generic error
          setError('Failed to sign in. Please check your credentials.')
        }
      }
    } catch (error) {
      console.error('Error during login process:', error)
      setError('An unexpected error occurred. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const handleEmailSignUp = async (email, password) => {
    setLoading(true)
    setError('')
    setNotification(null)

    try {
      // Check if the account is locked from previous failed attempts
      const lockStatus = await checkAccountLockStatus(email)
      if (lockStatus.locked) {
        setError(lockStatus.error)
        setLoading(false)
        return
      }

      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password)

        // Reset any login attempts for this email
        await resetLoginAttempts(email)

        // Send verification email
        await sendEmailVerification(userCredential.user)

        if (onAuthStateChange) {
          onAuthStateChange(userCredential.user)
        }

        setNotification({
          open: true,
          message: 'Account created! Please check your email to verify your account.',
          severity: 'success',
        })
      } catch (signUpError) {
        console.error('Sign-up error:', signUpError)

        // If the error is related to the email already being in use,
        // track it as a failed attempt to prevent enumeration attacks
        if (signUpError.code === 'auth/email-already-in-use') {
          await trackFailedLoginAttempt(email)
          setError('An account with this email already exists.')
        } else {
          setError(signUpError.message)
        }
      }
    } catch (error) {
      console.error('Error during sign-up process:', error)
      setError('An unexpected error occurred. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      if (onAuthStateChange) {
        onAuthStateChange(result.user)
      }
    } catch (error) {
      setError(`Error signing in with Google: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    const email = data.get('email')
    const password = data.get('password')

    if (email && password) {
      if (isSignUp) {
        handleEmailSignUp(email, password)
      } else {
        // Call handleEmailSignIn with email and password
        handleEmailSignIn(email, password)
      }
    } else {
      setError('Email and password are required')
    }
  }

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false })
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container component='main' maxWidth='xs'>
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component='h1' variant='h5'>
            {isSignUp ? 'Sign Up' : 'Sign In'}
          </Typography>
          <Box component='form' onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            {error && (
              <Alert severity='error' sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              margin='normal'
              required
              fullWidth
              id='email'
              label='Email Address'
              name='email'
              autoComplete='email'
              autoFocus
              disabled={loading}
            />
            <TextField
              margin='normal'
              required
              fullWidth
              name='password'
              label='Password'
              type='password'
              id='password'
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              disabled={loading}
            />
            <FormControlLabel control={<Checkbox value='remember' color='primary' />} label='Remember me' />
            <Button
              type='submit'
              fullWidth
              variant='contained'
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {isSignUp ? 'Sign Up' : 'Sign In'}
            </Button>

            <Divider sx={{ my: 2 }}>or</Divider>

            <Button fullWidth variant='outlined' startIcon={<GoogleIcon />} onClick={handleGoogleSignIn} disabled={loading} sx={{ mb: 2 }}>
              Continue with Google
            </Button>

            <Grid container>
              <Grid item xs>
                {!isSignUp && (
                  <Link href='#' variant='body2'>
                    Forgot password?
                  </Link>
                )}
              </Grid>
              <Grid item>
                <Link
                  href='#'
                  variant='body2'
                  onClick={(e) => {
                    e.preventDefault()
                    setIsSignUp(!isSignUp)
                    setError('')
                  }}
                >
                  {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Copyright sx={{ mt: 8, mb: 4 }} />
      </Container>
      <Snackbar
        open={notification?.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification?.severity}>
          {notification?.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  )
}
